import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Search } from "@mui/icons-material";
import CreatableSelect from "react-select/creatable";
import Select, { components } from "react-select";
import dayjs from "dayjs";

import { menuItemStyle, textfieldText } from "../../style/AppointmentStyle";
import {
  orangeButton,
  primaryBtnTeal,
  selectStyles,
  textfieldTextPrep,
} from "../../style/PatientDetailsStyle";
import { drTableGreen } from "../../style/PatinetFormStyle";
import { headingStyle, xBtn } from "../../style/LapPageStyle";

import useDebounce from "../../hooks/UseDebounce";
import UseSearchPatient from "../../helper/UseSearchPatient";
import UseFetchMedicalTestList from "../../helper/UseFetchMedicalTestList";
import LabTestListAll from "../../helper/LabTestListAll";

import {
  ExternalLabImageScan,
  externalLabReport,
  postMedicalReport,
} from "../../connections/postApi";
import { showToast } from "../../utils/toastUtil";
import { checkAvailableTest } from "../../connections/getApi";

const AiImageScanReport = ({ onClose, onSuccess }) => {
  const [inputValue, setInputValue] = useState("");
  const [patient, setPatient] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [openSearchDropDown, setOpenSearchDropDown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState({});
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [testSearch, setTestSearch] = useState("");
  const [createModal, setCreateModal] = useState({
    open: false,
    rowIndex: null,
    label: "",
    isSub: false,
    parentIndex: null,
    subIndex: null,
  });
  const [createChoice, setCreateChoice] = useState("normal");
  const [activeStep, setActiveStep] = useState(1);
  const originalValueRef = useRef({});

  const makeKey = (type, id) => `${type}_${id}`;

  const debouncedInputValue = useDebounce(inputValue, 600);
  const { data: searchResult, isLoading } = UseSearchPatient({
    q: debouncedInputValue,
    page: 1,
    perPage: 10,
  });

  const debouncedTestSearch = useDebounce(testSearch, 600);
  const isTyping = testSearch !== debouncedTestSearch;

  const {
    data: medicalTestLists,
    isLoading: medicalTestLoading,
    refetch: refetchMedicalTests,
  } = UseFetchMedicalTestList(1, 75, debouncedTestSearch);

  const testOptions =
    medicalTestLists?.payload?.medical_tests?.map((test) => ({
      value: test,
      label: test.name,
    })) || [];

  const { data: labData } = LabTestListAll();

  const formattedLabOptions = (labData?.payload?.labs || []).map((l) => ({
    value: l.id,
    label: l.name,
  }));

  const exactMatchExists = testOptions.some(
    (opt) =>
      opt.label.toLowerCase().trim() ===
      debouncedTestSearch.toLowerCase().trim(),
  );

  const shouldFetchTests = debouncedTestSearch?.trim().length > 0;

  const canCreate =
    debouncedTestSearch.length > 0 &&
    !isTyping &&
    !medicalTestLoading &&
    !exactMatchExists;

  const fileAccept = ".jpg,.jpeg,.pdf,.png";

  const isNewTestIncomplete = (row) => {
    return !row.lab_id || !row.min || !row.max || !row.critical;
  };

  const isNewSubTestIncomplete = (subRow) => {
    return !subRow?.lab_id || !subRow?.min || !subRow?.max || !subRow?.critical;
  };

  const isCompositeFromApi = (selected) => {
    const v = selected?.value;
    return Boolean(v?.composite === true);
  };

  const isLockedComposite = (row) => {
    const v = row?.test?.value;
    return Boolean(
      row?.isComposite &&
      !row?.test?.__isNew__ &&
      v?.composite_fields?.length > 0,
    );
  };

  const subTestOptions = useMemo(() => {
    return testOptions.filter((opt) => !(opt?.value?.composite === true));
  }, [testOptions]);

  const openCreateTypeModal = (
    rowIndex,
    label,
    isSub = false,
    parentIndex = null,
    subIndex = null,
  ) => {
    setCreateChoice("normal");
    setCreateModal({
      open: true,
      rowIndex,
      label,
      isSub,
      parentIndex,
      subIndex,
    });
  };

  const closeCreateTypeModal = () => {
    setCreateModal({
      open: false,
      rowIndex: null,
      label: "",
      isSub: false,
      parentIndex: null,
      subIndex: null,
    });
  };

  const confirmCreateType = () => {
    const { rowIndex, label, isSub, parentIndex, subIndex } = createModal;
    const updated = [...rows];

    if (isSub && parentIndex !== null && subIndex !== null) {
      updated[parentIndex].compositeRows[subIndex].test = {
        label,
        value: label,
        __isNew__: true,
      };
      updated[parentIndex].compositeRows[subIndex].lab_id =
        updated[parentIndex]?.lab_id ?? null;
      updated[parentIndex].compositeRows[subIndex].min = "";
      updated[parentIndex].compositeRows[subIndex].max = "";
      updated[parentIndex].compositeRows[subIndex].critical = "";
      setRows(updated);
      closeCreateTypeModal();
      return;
    }

    if (rowIndex === null) return;

    updated[rowIndex].test = { label, value: label, __isNew__: true };

    const makeComposite = createChoice === "composite";
    updated[rowIndex].isComposite = makeComposite;

    if (makeComposite) updated[rowIndex].value = "";
    if (!makeComposite) updated[rowIndex].compositeRows = [];

    setRows(updated);
    closeCreateTypeModal();
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        test: null,
        value: "",
        isComposite: false,
        compositeRows: [],
        ai_code: "",
        ai_name: "",
        unit: "",
        lab_id: null,
        min: "",
        max: "",
        critical: "",
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  const addCompositeSubRow = (parentIndex) => {
    const updated = [...rows];
    updated[parentIndex].compositeRows = [
      ...(updated[parentIndex].compositeRows || []),
      {
        test: null,
        value: "",
        lab_id: updated[parentIndex]?.lab_id ?? null,
        min: "",
        max: "",
        critical: "",
      },
    ];
    setRows(updated);
  };

  const removeCompositeSubRow = (parentIndex, subIndex) => {
    const updated = [...rows];
    const list = [...(updated[parentIndex].compositeRows || [])];
    list.splice(subIndex, 1);
    updated[parentIndex].compositeRows = list;
    setRows(updated);
  };

  // ✅ UPDATED: mapAiTestsToRows now accepts existingTests from checkAvailableTest API
  // Removed old findExistingTest helper — matching is now done inline using the API response
  const mapAiTestsToRows = (medicalTests = [], existingTests = []) => {
    return medicalTests.map((item) => {
      // Match by name OR code (case-insensitive) against API-returned existing tests
      const matched = existingTests.find(
        (t) =>
          (item?.name &&
            t?.name?.toLowerCase().trim() ===
              item?.name?.toLowerCase().trim()) ||
          (item?.code &&
            t?.code?.toLowerCase().trim() === item?.code?.toLowerCase().trim()),
      );

      const isComposite = Boolean(item?.composite);

      return {
        test: matched
          ? { label: matched.name, value: matched } // ✅ Exists in DB → existing test
          : {
              label: item?.name || item?.code || "",
              value: item?.name || item?.code || "",
              __isNew__: true, // 🆕 Not found → new test to be created
            },
        value: item?.amount ?? "",
        isComposite,
        compositeRows: (item?.composite_fields || []).map((sub) => {
          // Also check sub-tests against existing API results
          const matchedSub = existingTests.find(
            (t) =>
              sub?.name &&
              t?.name?.toLowerCase().trim() === sub?.name?.toLowerCase().trim(),
          );
          return {
            test: matchedSub
              ? { label: matchedSub.name, value: matchedSub }
              : { label: sub?.name || "", value: sub },
            value: "",
            lab_id: null,
            min: "",
            max: "",
            critical: "",
          };
        }),
        ai_code: item?.code || "",
        ai_name: item?.name || "",
        min: item?.female_min ?? item?.male_min ?? "",
        max: item?.female_max ?? item?.male_max ?? "",
        critical: "",
        unit: item?.unit || "",
        lab_id: null,
      };
    });
  };

  // ✅ UPDATED: handleScanUpload now calls checkAvailableTest API after scanning
  const handleScanUpload = async () => {
    if (!patient) {
      showToast("Please select a patient first", "ERROR");
      return;
    }

    if (!file) {
      showToast("Please select a file", "ERROR");
      return;
    }

    const formData = new FormData();
    formData.append("type", "medical_test");
    formData.append("file", file);

    try {
      setScanLoading(true);

      // Step 1: Scan the image / PDF via AI
      const res = await ExternalLabImageScan(formData);
      const medicalTests = res?.payload?.ai_summary?.data?.medical_tests || [];

      if (!medicalTests.length) {
        showToast("No result found", "ERROR");
        return;
      }

      // Step 2: Extract all scanned test names and call checkAvailableTest API
      // This tells us which tests already exist in the DB vs need to be created
      const testNames = medicalTests.map((t) => t?.name).filter(Boolean);

      let existingTests = [];
      if (testNames.length > 0) {
        const checkRes = await checkAvailableTest(testNames);
        existingTests = checkRes?.payload?.medical_tests || [];
      }

      // Step 3: Map AI-scanned tests → rows, marking each as existing or new
      const prefilledRows = mapAiTestsToRows(medicalTests, existingTests);
      setRows(prefilledRows);
      setActiveStep(2);
      showToast("Tests scanned successfully", "SUCCESS");
    } catch (err) {
      console.error(err);
      showToast("Failed to scan report", "ERROR");
    } finally {
      setScanLoading(false);
    }
  };

  const handleBackToUpload = () => {
    setActiveStep(1);
    setRows([]);
    setFile(null);
  };

  const handleValueBlur = async (row, index) => {
    if (!patient) {
      showToast("Please select a patient first", "ERROR");
      return;
    }

    if (!row.test || !row.value) return;
    if (row.isComposite) return;
    if (row.test?.__isNew__) return; // new tests use handleCreateNormalTest button

    console.log(row, "row");
    // ✅ Already created → call UPDATE API
    if (row?.reportId) {
      const item = {
        id: row.reportId,
        report_id: row.reportId,
        patient_id: patient?.id,
        medical_test_id: row.test?.value?.id,
        invoice_item_id: row.test?.value?.invoice_item_id ?? null,
        value: row.value,
      };
      // store original for comparison
      // const key = makeKey("normal", row.reportId);
      // if (originalValueRef.current[key] === undefined) {
      //   originalValueRef.current[key] = row.value;
      // }
      await handleEditMedicalReportValue(item, row.value);
      return;
    }

    // ✅ First time → CREATE
    const reportObject = {
      patient_id: patient?.id,
      name: row.test.value.name,
      code: row.test.value.code,
      value: row.value,
      generated_at: generatedAt,
      lab_id: row.test.value.lab_id,
      medical_test_category_id: row.test.value.medical_test_category_id,
    };

    const body = { medical_test_reports: [reportObject] };

    setRowLoading((prev) => ({ ...prev, [index]: true }));

    try {
      const res = await externalLabReport(body);

      showToast(
        res?.status?.message || res?.status?.status,
        res?.status?.status,
      );

      // ✅ Store reportId so future blurs call UPDATE
      const created = res?.payload?.[0];
      // console.log(created, "Created");
      if (created?.id) {
        const updated = [...rows];
        updated[index].reportId = created?.id;
        setRows(updated);
      }
    } catch (err) {
      console.error("API ERROR:", err);
    } finally {
      setRowLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleCreateNormalTest = async (row, index) => {
    if (!patient) {
      showToast("Please select a patient first", "ERROR");
      return;
    }

    if (!row?.test?.__isNew__) return;
    if (row.isComposite) return;

    if (!row.value) {
      showToast("Please enter value", "ERROR");
      return;
    }

    if (isNewTestIncomplete(row)) {
      showToast("Please fill Lab, Min, Max & Critical Range", "ERROR");
      return;
    }

    const reportObject = {
      patient_id: patient?.id,
      name: row.test.label,
      code: row.ai_code || row.test.label,
      value: row.value,
      generated_at: generatedAt,
      lab_id: row?.lab_id?.value,
      min_range: row.min || null,
      max_range: row.max || null,
      critical_range: row.critical || null,
    };

    const body = { medical_test_reports: [reportObject] };

    setRowLoading((prev) => ({ ...prev, [`create_${index}`]: true }));

    try {
      const res = await externalLabReport(body);
      const created = res?.payload?.[0];

      if (res?.status?.status === "SUCCESS" && created?.medical_test_id) {
        const updated = [...rows];

        updated[index].test = {
          label: created.test_name || row.test.label,
          value: {
            id: created.medical_test_id,
            name: created.test_name || row.test.label,
            code: created.code || row.ai_code || row.test.label,
            lab_id: created.lab_id || row?.lab_id?.value,
            medical_test_category_id: created.medical_test_category_id ?? null,
          },
        };

        setRows(updated);
      }

      showToast(
        res?.status?.message || res?.status?.status,
        res?.status?.status,
      );
    } catch (err) {
      console.error("API ERROR:", err);
    } finally {
      setRowLoading((prev) => ({ ...prev, [`create_${index}`]: false }));
    }
  };

  const handleEditMedicalReportValue = async (
    item,
    newValue,
    force = false,
  ) => {
    if (newValue === undefined || newValue === null) return;

    const key = makeKey("normal", item?.id);
    const oldValue = originalValueRef.current[key];
    const prevValue = oldValue !== undefined ? oldValue : (item?.value ?? "");

    if (!force && String(prevValue) === String(newValue)) {
      delete originalValueRef.current[key];
      return;
    }

    const payload = {
      medical_test_report: {
        patient_id: item?.patient_id,
        medical_test_id: item?.medical_test_id,
        invoice_item_id: item?.invoice_item_id,
        value: newValue,
      },
      id: item?.id,
    };

    try {
      const result = await postMedicalReport(
        payload,
        item?.report_id ?? item?.id,
      );
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch (e) {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key];
    }
  };

  // =============================================================
  // ✅ UPDATE: Composite sub-test (called when reportId already exists)
  // =============================================================
  const handleEditMedicalReportValueComp = async (
    item,
    newValue,
    force = false,
  ) => {
    if (newValue === undefined || newValue === null) return;

    const key = makeKey("comp", item?.id);
    const oldValue = originalValueRef.current[key];
    const prevValue = oldValue !== undefined ? oldValue : (item?.value ?? "");

    if (!force && String(prevValue) === String(newValue)) {
      delete originalValueRef.current[key];
      return;
    }

    const payload = {
      medical_test_report: {
        patient_id: item?.patient_id,
        medical_test_id: item?.medical_test_id,
        invoice_item_id: item?.invoice_item_id,
        value: newValue,
        composite_parent_test_id: item?.composite_parent_test_id,
      },
      ...(item?.medical_test_report_id && { id: item?.medical_test_report_id }),
    };

    try {
      const result = await postMedicalReport(
        payload,
        item?.report_id ?? item?.medical_test_report_id ?? item?.id,
      );
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch (e) {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key];
    }
  };

  const buildCompositeMedicalTestPayload = (parentRow) => {
    return {
      code: parentRow.ai_code || parentRow.test?.label,
      name: parentRow.test?.label,
      lab_id: parentRow?.lab_id?.value,
      female_min: parentRow.female_min ?? parentRow.min ?? null,
      male_min: parentRow.male_min ?? parentRow.min ?? null,
      female_max: parentRow.female_max ?? parentRow.max ?? null,
      male_max: parentRow.male_max ?? parentRow.max ?? null,
      composite_fields: [],
      composite: true,
    };
  };

  const getCompositeParentTestId = (parentRow) => {
    if (!parentRow?.test?.__isNew__ && parentRow?.test?.value?.id) {
      return parentRow.test.value.id;
    }
    if (parentRow?.composite_parent_test_id) {
      return parentRow.composite_parent_test_id;
    }
    return null;
  };

  const handleCreateCompositeAndReports = async (parentIndex) => {
    const parent = rows[parentIndex];

    if (!patient) return showToast("Please select a patient first", "ERROR");
    if (!parent?.test || !parent.isComposite) return;

    if (parent?.test?.__isNew__ && !parent?.lab_id) {
      return showToast("Please choose lab for composite test", "ERROR");
    }

    const subs = parent.compositeRows || [];
    const validSubs = subs.filter((s) => s?.test && s?.value);

    if (validSubs.length === 0) {
      return showToast("Please add at least 1 sub-test value", "ERROR");
    }

    const hasInvalidNewSub = validSubs.some(
      (s) => s?.test?.__isNew__ && isNewSubTestIncomplete(s),
    );

    if (hasInvalidNewSub) {
      return showToast(
        "Please fill Lab, Min, Max & Critical for new sub-tests",
        "ERROR",
      );
    }

    const body = {
      ...(parent.test?.__isNew__ && {
        composite_medical_test: buildCompositeMedicalTestPayload(parent),
      }),
      medical_test_reports: validSubs.map((sub) => {
        const isNewSub = sub?.test?.__isNew__;

        return {
          patient_id: patient?.id,
          name: isNewSub ? sub.test.label : sub.test.value.name,
          code: isNewSub ? sub.ai_code || sub.test.label : sub.test.value.code,
          value: sub.value,
          generated_at: generatedAt,
          composite_parent_test_id: parent.test?.__isNew__
            ? ""
            : getCompositeParentTestId(parent),
          ...(isNewSub
            ? {
                lab_id: sub?.lab_id?.value,
                medical_test_category_id: null,
                min_range: sub.min || null,
                max_range: sub.max || null,
                critical_range: sub.critical || null,
              }
            : {
                lab_id: sub.test.value.lab_id,
                medical_test_category_id:
                  sub.test.value.medical_test_category_id,
              }),
        };
      }),
    };

    setRowLoading((prev) => ({
      ...prev,
      [`comp_create_${parentIndex}`]: true,
    }));

    try {
      const res = await externalLabReport(body);

      if (res?.status?.status !== "SUCCESS") {
        showToast(res?.status?.message || "Failed", "ERROR");
        return;
      }

      if (parent.test?.__isNew__) {
        const refetchRes = await refetchMedicalTests?.();
        const latestTests =
          refetchRes?.data?.payload?.medical_tests ||
          medicalTestLists?.payload?.medical_tests ||
          [];

        const createdComposite = latestTests.find(
          (t) =>
            (t?.name || "").toLowerCase().trim() ===
              parent.test.label.toLowerCase().trim() && t?.composite === true,
        );

        if (createdComposite?.id) {
          const updated = [...rows];
          updated[parentIndex].test = {
            label: createdComposite.name,
            value: createdComposite,
          };
          updated[parentIndex].composite_parent_test_id = createdComposite.id;
          setRows(updated);
        }
      }

      showToast(res?.status?.message || "Composite saved", "SUCCESS");
    } catch (err) {
      console.error("API ERROR:", err);
      showToast("Failed to save composite", "ERROR");
    } finally {
      setRowLoading((prev) => ({
        ...prev,
        [`comp_create_${parentIndex}`]: false,
      }));
    }
  };

  const handleCompositeValueBlur = async (parentRowIndex, subRowIndex) => {
    const parent = rows[parentRowIndex];
    const sub = parent?.compositeRows?.[subRowIndex];

    if (!patient) return showToast("Please select a patient first", "ERROR");
    if (!parent?.test || !parent.isComposite) return;
    if (!sub?.test || !sub?.value) return;

    const parentId = getCompositeParentTestId(parent);
    if (!parentId) return; // new composite parent → skip blur, use Save button

    const isNewSub = sub?.test?.__isNew__;
    if (isNewSub && isNewSubTestIncomplete(sub)) return;

    // ✅ Already created → call UPDATE API
    if (sub.reportId) {
      const item = {
        id: sub.reportId,
        report_id: sub.reportId,
        medical_test_report_id: sub.reportId,
        patient_id: patient?.id,
        medical_test_id: isNewSub ? sub.test?.value?.id : sub.test?.value?.id,
        invoice_item_id: sub.test?.value?.invoice_item_id ?? null,
        composite_parent_test_id: parentId,
        value: sub.value,
      };

      await handleEditMedicalReportValueComp(item, sub.value);
      return;
    }

    // ✅ First time → CREATE
    const body = {
      medical_test_reports: [
        {
          patient_id: patient?.id,
          name: isNewSub ? sub.test.label : sub.test.value.name,
          code: isNewSub ? sub.test.label : sub.test.value.code,
          value: sub.value,
          generated_at: generatedAt,
          composite_parent_test_id: parentId,
          ...(isNewSub
            ? {
                lab_id: sub?.lab_id?.value,
                medical_test_category_id: null,
                min_range: sub.min || null,
                max_range: sub.max || null,
                critical_range: sub.critical || null,
              }
            : {
                lab_id: sub.test.value.lab_id,
                medical_test_category_id:
                  sub.test.value.medical_test_category_id,
              }),
        },
      ],
    };

    const key = `c_${parentRowIndex}_${subRowIndex}`;
    setRowLoading((prev) => ({ ...prev, [key]: true }));

    try {
      const res = await externalLabReport(body);

      showToast(res?.status?.message || "Saved", res?.status?.status);

      // ✅ Store reportId so future blurs call UPDATE
      const created = res?.payload?.[0];
      if (created?.id) {
        const updated = [...rows];
        updated[parentRowIndex].compositeRows[subRowIndex].reportId =
          created.id;
        setRows(updated);
      }
    } catch (err) {
      console.error("API ERROR:", err);
    } finally {
      setRowLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitAll = async () => {
    if (!patient) return showToast("Please select a patient first", "ERROR");
    if (!rows.length) return showToast("Please add at least one test", "ERROR");

    const invalidNormal = rows.some(
      (row) =>
        !row.isComposite &&
        row?.test?.__isNew__ &&
        (!row.value || isNewTestIncomplete(row)),
    );

    if (invalidNormal) {
      return showToast(
        "Please complete all new manual tests before submit",
        "ERROR",
      );
    }

    const invalidComposite = rows.some((row) => {
      if (!row.isComposite) return false;
      if (row.test?.__isNew__ && !row.lab_id) return true;

      const validSubs = (row.compositeRows || []).filter((s) => s?.test);
      if (!validSubs.length) return true;

      return validSubs.some(
        (s) => !s.value || (s?.test?.__isNew__ && isNewSubTestIncomplete(s)),
      );
    });

    if (invalidComposite) {
      return showToast(
        "Please complete composite tests and sub-tests before submit",
        "ERROR",
      );
    }

    const normalRows = rows.filter((r) => !r.isComposite);
    const newCompositeRows = rows.filter(
      (r) => r.isComposite && r.test?.__isNew__,
    );

    if (normalRows.length > 0) {
      const reports = normalRows.map((row) => {
        const isNew = row.test?.__isNew__;
        return {
          patient_id: patient?.id,
          name: isNew ? row.test.label : row.test.value.name,
          code: isNew ? row.ai_code || row.test.label : row.test.value.code,
          value: row.value,
          generated_at: generatedAt,
          ...(isNew
            ? {
                lab_id: row?.lab_id?.value,
                medical_test_category_id: null,
                min_range: row.min || null,
                max_range: row.max || null,
                critical_range: row.critical || null,
              }
            : {
                lab_id: row.test.value.lab_id,
                medical_test_category_id:
                  row.test.value.medical_test_category_id,
              }),
        };
      });

      try {
        setLoading(true);
        const res = await externalLabReport({ medical_test_reports: reports });
        if (res?.status?.status !== "SUCCESS") {
          showToast(res?.status?.message || "Failed", "ERROR");
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error(err);
        showToast("Failed to save tests", "ERROR");
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    for (let i = 0; i < newCompositeRows.length; i++) {
      const actualIndex = rows.findIndex((r) => r === newCompositeRows[i]);
      // eslint-disable-next-line no-await-in-loop
      await handleCreateCompositeAndReports(actualIndex);
    }

    showToast("Report submitted successfully", "SUCCESS");
    onSuccess?.();
    onClose?.();
  };

  return (
    <Box
      sx={{
        minHeight: "92vh",
        p: 1,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
        overflow: "auto",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
        mt={-0.5}
      >
        <Box />
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <AddIcon
            sx={{
              width: "36px",
              height: "36px",
              color: "neutral.teal",
              marginBottom: 1,
            }}
          />
          <Typography
            variant="heading1"
            fontWeight={600}
            gutterBottom
            sx={{ ...headingStyle }}
          >
            AI Image Scan Report
          </Typography>
        </Box>
        <IconButton sx={{ marginBottom: "0.5rem" }} onClick={onClose}>
          <CloseIcon size="small" sx={{ color: "red" }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mt: 1,
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 65%" } }}>
            <Autocomplete
              popupIcon={false}
              clearIcon={false}
              clearOnBlur={false}
              onChange={(event, newValue) => {
                if (newValue) {
                  setInputValue("");
                  setPatient(newValue);
                }
              }}
              open={
                openSearchDropDown &&
                Boolean(inputValue?.trim()) &&
                searchResult?.payload?.patients?.length > 0
              }
              noOptionsText=""
              onOpen={() => setOpenSearchDropDown(true)}
              onClose={() => setOpenSearchDropDown(false)}
              onInputChange={(e, value) => setInputValue(value)}
              options={searchResult?.payload?.patients ?? []}
              filterOptions={(options) => options}
              getOptionLabel={(option) =>
                option.uid && option.full_name
                  ? `${option.uid} - ${option.full_name}`
                  : ""
              }
              renderOption={(props, option) => (
                <MenuItem key={option.id} {...props} sx={{ ...menuItemStyle }}>
                  {`${option.uid} - ${option.full_name}`}
                </MenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  placeholder="Search Patient Id, Name, Mobile, Email"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <SearchIcon sx={{ opacity: 0.4, mr: -1 }} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    ...textfieldTextPrep,
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Lato",
                      borderRadius: "8px",
                      height: "45px",
                    },
                  }}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 20%" },
              minWidth: "150px",
            }}
          >
            <TextField
              fullWidth
              type="date"
              label="Select Date"
              value={generatedAt}
              onChange={(e) => setGeneratedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Lato",
                  borderRadius: "8px",
                  height: "45px",
                },
              }}
            />
          </Box>

          {activeStep === 1 && (
            <Box
              sx={{
                width: "100%",
                mt: 3,
                border: "1px dashed #D0D5DD",
                borderRadius: "16px",
                background: "#F9FBFF",
                px: 2,
                py: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 2,
                minHeight: "260px",
              }}
            >
              <FileUploadOutlinedIcon
                sx={{ fontSize: 52, color: "neutral.teal" }}
              />

              <Typography
                sx={{ fontSize: "20px", fontWeight: 600, color: "#101828" }}
              >
                Upload Image / PDF
              </Typography>

              <Typography sx={{ color: "#667085", maxWidth: "420px" }}>
                Upload the lab report image or PDF to scan and prefill the
                medical test form.
              </Typography>

              <Button
                component="label"
                sx={{
                  ...primaryBtnTeal,
                  gap: 1,
                  height: "45px",
                  px: 3,
                  borderRadius: "10px",
                }}
                disabled={scanLoading}
              >
                <FileUploadOutlinedIcon />
                Choose File
                <input
                  type="file"
                  hidden
                  accept={fileAccept}
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>

              {file && (
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    background: "#fff",
                    minWidth: "320px",
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    Selected File
                  </Typography>
                  <Typography sx={{ color: "#667085", mt: 0.5 }}>
                    {file.name}
                  </Typography>
                </Box>
              )}

              <Button
                onClick={handleScanUpload}
                disabled={!file || scanLoading}
                sx={{
                  ...primaryBtnTeal,
                  height: "45px",
                  minWidth: "180px",
                  borderRadius: "10px",
                }}
              >
                {scanLoading ? "Scanning..." : "Scan Report"}
              </Button>
            </Box>
          )}
        </Box>

        {activeStep === 2 && (
          <>
            <Box
              mt={3}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "15px",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  ...drTableGreen,
                  backgroundColor: "neutral.backGround",
                  py: 1,
                  px: 1,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item md={0.2}>
                    <Typography variant="body1_text_bold">S.No</Typography>
                  </Grid>
                  <Grid item md={6} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ textAlign: "center" }}
                    >
                      Test Names
                    </Typography>
                  </Grid>
                  <Grid item md={5.3} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ textAlign: "center" }}
                    >
                      Value
                    </Typography>
                  </Grid>
                  {rows?.length > 0 && (
                    <Grid item md={0.5}>
                      <IconButton
                        id="addMoreBtn"
                        variant="contained"
                        sx={{
                          ...orangeButton,
                          display: "flex",
                          marginLeft: "auto",
                        }}
                        onClick={handleAddRow}
                      >
                        <AddIcon
                          sx={{
                            width: "26px",
                            color: "neutral.white",
                            height: "26px",
                            padding: "2px",
                          }}
                        />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box px={2} py={1}>
                {rows?.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      border: "1px dashed #D0D5DD",
                      borderRadius: "12px",
                      background: "#F9FBFF",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#667085",
                        fontSize: "15px",
                        fontWeight: 500,
                      }}
                    >
                      No tests added yet
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddRow}
                      sx={{
                        ...primaryBtnTeal,
                        height: "40px",
                        borderRadius: "10px",
                        px: 3,
                      }}
                    >
                      Add Manual Test
                    </Button>
                  </Box>
                ) : (
                  rows?.map((row, index) => {
                    const isNew = row.test?.__isNew__;
                    const locked = isLockedComposite(row);
                    return (
                      <Box key={index}>
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{
                            py: 1,
                            borderBottom:
                              index !== rows.length - 1
                                ? "1px solid #E1E1E1"
                                : "none",
                          }}
                        >
                          <Grid item xs={12} md={0.2}>
                            <Typography>{index + 1}</Typography>
                          </Grid>

                          {/* Test Name */}
                          <Grid
                            item
                            xs={12}
                            md={
                              !row.test?.__isNew__ && row.isComposite ? 11.3 : 6
                            }
                          >
                            <CreatableSelect
                              components={{
                                DropdownIndicator: (props) => (
                                  <components.DropdownIndicator {...props}>
                                    <Search sx={{ color: "#999" }} />
                                  </components.DropdownIndicator>
                                ),
                                IndicatorSeparator: () => null,
                                LoadingIndicator: () => null,
                              }}
                              isSearchable
                              noOptionsMessage={() => {
                                if (isTyping) return "Loading...";
                                if (medicalTestLoading)
                                  return "Searching tests...";
                                if (debouncedTestSearch)
                                  return "No tests found";
                                return "Start typing to search";
                              }}
                              isValidNewOption={() => canCreate}
                              onCreateOption={(val) =>
                                openCreateTypeModal(index, val)
                              }
                              isMulti={false}
                              isLoading={medicalTestLoading}
                              options={shouldFetchTests ? testOptions : []}
                              value={row.test}
                              placeholder="Select or Create Test"
                              onInputChange={(val) => setTestSearch(val)}
                              onChange={(selected) => {
                                const updated = [...rows];
                                updated[index].test = selected;
                                updated[index].reportId = null; // ✅ reset on test change

                                const existingComposite =
                                  isCompositeFromApi(selected);
                                updated[index].isComposite = existingComposite;

                                if (existingComposite) {
                                  updated[index].value = "";
                                  updated[index].compositeRows = (
                                    selected?.value?.composite_fields || []
                                  ).map((t) => ({
                                    test: { value: t, label: t.name },
                                    value: "",
                                    reportId: null, // ✅
                                  }));
                                } else {
                                  updated[index].compositeRows = [];
                                }

                                setRows(updated);
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuPlacement="auto"
                              maxMenuHeight={220}
                              menuShouldScrollIntoView={false}
                              styles={selectStyles}
                            />
                          </Grid>

                          {/* Value */}
                          {row.isComposite ? (
                            row.test?.__isNew__ && (
                              <Grid item xs={12} md={5.3}>
                                <Select
                                  components={{
                                    DropdownIndicator: (props) => (
                                      <components.DropdownIndicator {...props}>
                                        <Search sx={{ color: "#999" }} />
                                      </components.DropdownIndicator>
                                    ),
                                    IndicatorSeparator: () => null,
                                  }}
                                  options={formattedLabOptions}
                                  value={row.lab_id}
                                  onChange={(selected) => {
                                    const updated = [...rows];
                                    updated[index].lab_id = selected;
                                    setRows(updated);
                                  }}
                                  placeholder="Choose lab"
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                />
                              </Grid>
                            )
                          ) : (
                            <Grid item xs={12} md={5.3}>
                              <TextField
                                fullWidth
                                placeholder="Value"
                                value={row?.value}
                                disabled={!row.test || rowLoading[index]}
                                sx={{
                                  ...textfieldText,
                                  borderRadius: "10px",
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    height: "45px",
                                    fontFamily: "Lato",
                                  },
                                }}
                                InputProps={{
                                  endAdornment: rowLoading[index] ? (
                                    <CircularProgress
                                      size={20}
                                      sx={{ mr: 1 }}
                                    />
                                  ) : null,
                                }}
                                onChange={(e) => {
                                  const updated = [...rows];
                                  updated[index].value = e.target.value;
                                  setRows(updated);
                                }}
                                onFocus={() => {
                                  if (row?.reportId) {
                                    const key = makeKey("normal", row.reportId);
                                    if (
                                      originalValueRef.current[key] ===
                                      undefined
                                    ) {
                                      originalValueRef.current[key] =
                                        row.value ?? "";
                                    }
                                  }
                                }}
                                onBlur={() => {
                                  if (row.test?.__isNew__) return;
                                  handleValueBlur(row, index);
                                }}
                              />
                            </Grid>
                          )}

                          {/* Delete Row */}
                          <Grid
                            item
                            xs={12}
                            md={0.5}
                            display="flex"
                            justifyContent="center"
                          >
                            <IconButton
                              sx={{
                                marginBottom: "7px",
                                height: "28px",
                                display: "flex",
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                              }}
                              onClick={() => handleRemoveRow(index)}
                            >
                              <CloseIcon
                                sx={{
                                  ...xBtn,
                                  width: "26px",
                                  height: "26px",
                                  padding: "2px",
                                }}
                              />
                            </IconButton>
                          </Grid>

                          {/* Extra fields for new normal test */}
                          {isNew && !row.isComposite && (
                            <Grid
                              container
                              spacing={2}
                              sx={{ pb: 2, pt: 1, pl: { xs: 0, md: 4 } }}
                            >
                              <Grid item xs={12} md={4.5}>
                                <Select
                                  components={{
                                    DropdownIndicator: (props) => (
                                      <components.DropdownIndicator {...props}>
                                        <Search sx={{ color: "#999" }} />
                                      </components.DropdownIndicator>
                                    ),
                                    IndicatorSeparator: () => null,
                                  }}
                                  options={formattedLabOptions}
                                  value={row.lab_id}
                                  onChange={(selected) => {
                                    const updated = [...rows];
                                    updated[index].lab_id = selected;
                                    setRows(updated);
                                  }}
                                  placeholder="Choose lab"
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                />
                              </Grid>

                              <Grid item xs={6} md={2}>
                                <TextField
                                  fullWidth
                                  placeholder="Min Range"
                                  value={row.min || ""}
                                  sx={{
                                    ...textfieldText,
                                    borderRadius: "10px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "10px",
                                      fontFamily: "Lato",
                                      height: "45px",
                                    },
                                  }}
                                  onChange={(e) => {
                                    const updated = [...rows];
                                    updated[index].min = e.target.value;
                                    setRows(updated);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={6} md={2}>
                                <TextField
                                  fullWidth
                                  placeholder="Max Range"
                                  value={row.max || ""}
                                  sx={{
                                    ...textfieldText,
                                    borderRadius: "10px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "10px",
                                      height: "45px",
                                      fontFamily: "Lato",
                                    },
                                  }}
                                  onChange={(e) => {
                                    const updated = [...rows];
                                    updated[index].max = e.target.value;
                                    setRows(updated);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  placeholder="Critical Range"
                                  value={row.critical || ""}
                                  sx={{
                                    ...textfieldText,
                                    borderRadius: "10px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "10px",
                                      height: "45px",
                                      fontFamily: "Lato",
                                    },
                                  }}
                                  onChange={(e) => {
                                    const updated = [...rows];
                                    updated[index].critical = e.target.value;
                                    setRows(updated);
                                  }}
                                />
                              </Grid>

                              {!row?.isComposite && (
                                <Grid item xs={12} md={3}>
                                  <Button
                                    sx={{
                                      ...primaryBtnTeal,
                                      height: "45px",
                                      borderRadius: "10px",
                                    }}
                                    fullWidth
                                    disabled={
                                      isNewTestIncomplete(row) ||
                                      !row.value ||
                                      rowLoading[`create_${index}`]
                                    }
                                    onClick={() =>
                                      handleCreateNormalTest(row, index)
                                    }
                                  >
                                    {rowLoading[`create_${index}`]
                                      ? "Creating..."
                                      : "Create test"}
                                  </Button>
                                </Grid>
                              )}
                            </Grid>
                          )}
                        </Grid>

                        {/* Composite Sub Table */}
                        {row.isComposite && (
                          <Box
                            sx={{
                              mt: 1,
                              mb: 2,
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                              overflow: "hidden",
                              background: "#fff",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1,
                                background: "#F9FBFF",
                                borderBottom: "1px solid #E5E7EB",
                              }}
                            >
                              <Typography
                                sx={{ fontFamily: "Lato", fontWeight: 600 }}
                              >
                                Sub Tests
                              </Typography>

                              <Box sx={{ display: "flex", gap: 2 }}>
                                {!locked && (
                                  <Button
                                    onClick={() => addCompositeSubRow(index)}
                                    startIcon={<AddIcon />}
                                    sx={{
                                      ...primaryBtnTeal,
                                      height: "34px",
                                      borderRadius: "10px",
                                      px: 2,
                                    }}
                                    disabled={!row.test}
                                  >
                                    Add Sub Test
                                  </Button>
                                )}

                                {row.test?.__isNew__ && (
                                  <Button
                                    onClick={() =>
                                      handleCreateCompositeAndReports(index)
                                    }
                                    sx={{
                                      ...primaryBtnTeal,
                                      height: "34px",
                                      borderRadius: "10px",
                                      px: 2,
                                    }}
                                    disabled={
                                      rowLoading[`comp_create_${index}`]
                                    }
                                  >
                                    {rowLoading[`comp_create_${index}`]
                                      ? "Saving..."
                                      : "Save Composite"}
                                  </Button>
                                )}
                              </Box>
                            </Box>

                            <TableContainer sx={{ p: 1 }}>
                              <Table
                                size="small"
                                sx={{
                                  "& .MuiTableCell-root": {
                                    borderBottom: "none",
                                  },
                                  "& .MuiTableHead-root .MuiTableCell-root": {
                                    borderBottom: "1px solid #E5E7EB",
                                  },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{ fontWeight: 700, maxWidth: "16px" }}
                                    >
                                      S.No
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 700, width: "55%" }}
                                    >
                                      Test Name
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 700, width: "35%" }}
                                    >
                                      Value
                                    </TableCell>
                                    {!locked && (
                                      <TableCell
                                        sx={{ fontWeight: 700, width: "10%" }}
                                      >
                                        Action
                                      </TableCell>
                                    )}
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {(row.compositeRows || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={4}>
                                        <Typography
                                          sx={{
                                            color: "#667085",
                                            fontFamily: "Lato",
                                          }}
                                        >
                                          No sub-tests added yet.
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    (row.compositeRows || []).map(
                                      (subRow, subIndex) => {
                                        const key = `c_${index}_${subIndex}`;
                                        const isNewSub = Boolean(
                                          subRow?.test?.__isNew__,
                                        );

                                        return (
                                          <React.Fragment key={key}>
                                            <TableRow>
                                              <TableCell
                                                sx={{
                                                  fontWeight: 700,
                                                  maxWidth: "16px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {subIndex + 1}
                                              </TableCell>

                                              <TableCell>
                                                {locked ? (
                                                  <Box
                                                    sx={{
                                                      height: "40px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      px: 1,
                                                      borderRadius: "10px",
                                                      border:
                                                        "1px solid #E5E7EB",
                                                      background: "#F9FBFF",
                                                    }}
                                                  >
                                                    <Typography
                                                      sx={{
                                                        fontFamily: "Lato",
                                                      }}
                                                    >
                                                      {subRow?.test?.label ||
                                                        subRow?.test?.value
                                                          ?.name}
                                                    </Typography>
                                                  </Box>
                                                ) : (
                                                  <CreatableSelect
                                                    components={{
                                                      DropdownIndicator: (
                                                        props,
                                                      ) => (
                                                        <components.DropdownIndicator
                                                          {...props}
                                                        >
                                                          <Search
                                                            sx={{
                                                              color: "#999",
                                                            }}
                                                          />
                                                        </components.DropdownIndicator>
                                                      ),
                                                      IndicatorSeparator: () =>
                                                        null,
                                                      LoadingIndicator: () =>
                                                        null,
                                                    }}
                                                    isSearchable
                                                    isMulti={false}
                                                    isValidNewOption={() =>
                                                      canCreate
                                                    }
                                                    isLoading={
                                                      medicalTestLoading
                                                    }
                                                    options={
                                                      shouldFetchTests
                                                        ? subTestOptions
                                                        : []
                                                    }
                                                    value={subRow.test}
                                                    placeholder="Select or Create Sub Test"
                                                    onInputChange={(val) =>
                                                      setTestSearch(val)
                                                    }
                                                    onChange={(selected) => {
                                                      const updated = [...rows];
                                                      updated[
                                                        index
                                                      ].compositeRows[
                                                        subIndex
                                                      ].test = selected;
                                                      updated[
                                                        index
                                                      ].compositeRows[
                                                        subIndex
                                                      ].reportId = null; // ✅ reset on test change

                                                      if (
                                                        selected?.__isNew__ ||
                                                        selected?.value ===
                                                          selected?.label
                                                      ) {
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].lab_id =
                                                          updated[index]
                                                            ?.lab_id ?? null;
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].min = "";
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].max = "";
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].critical = "";
                                                      }

                                                      setRows(updated);
                                                    }}
                                                    menuPortalTarget={
                                                      document.body
                                                    }
                                                    styles={selectStyles}
                                                  />
                                                )}
                                              </TableCell>

                                              <TableCell>
                                                <TextField
                                                  fullWidth
                                                  placeholder="Value"
                                                  value={subRow.value || ""}
                                                  disabled={
                                                    !subRow.test ||
                                                    rowLoading[key]
                                                  }
                                                  sx={{
                                                    ...textfieldText,
                                                    borderRadius: "10px",
                                                    "& .MuiOutlinedInput-root":
                                                      {
                                                        borderRadius: "10px",
                                                        height: "40px",
                                                        fontFamily: "Lato",
                                                      },
                                                  }}
                                                  InputProps={{
                                                    endAdornment: rowLoading[
                                                      key
                                                    ] ? (
                                                      <CircularProgress
                                                        size={18}
                                                        sx={{ mr: 1 }}
                                                      />
                                                    ) : null,
                                                  }}
                                                  onChange={(e) => {
                                                    const updated = [...rows];
                                                    updated[
                                                      index
                                                    ].compositeRows[
                                                      subIndex
                                                    ].value = e.target.value;
                                                    setRows(updated);
                                                  }}
                                                  onBlur={() => {
                                                    if (
                                                      rows[index]?.test
                                                        ?.__isNew__
                                                    )
                                                      return;
                                                    handleCompositeValueBlur(
                                                      index,
                                                      subIndex,
                                                    );
                                                  }}
                                                  onFocus={() => {
                                                    if (subRow?.reportId) {
                                                      const key = makeKey(
                                                        "comp",
                                                        subRow.reportId,
                                                      );
                                                      if (
                                                        originalValueRef
                                                          .current[key] ===
                                                        undefined
                                                      ) {
                                                        originalValueRef.current[
                                                          key
                                                        ] = subRow.value ?? "";
                                                      }
                                                    }
                                                  }}
                                                />
                                              </TableCell>

                                              <TableCell>
                                                {!locked && (
                                                  <IconButton
                                                    onClick={() =>
                                                      removeCompositeSubRow(
                                                        index,
                                                        subIndex,
                                                      )
                                                    }
                                                  >
                                                    <CloseIcon
                                                      sx={{
                                                        ...xBtn,
                                                        width: 22,
                                                        height: 22,
                                                      }}
                                                    />
                                                  </IconButton>
                                                )}
                                              </TableCell>
                                            </TableRow>

                                            {/* Extra fields for new sub-test */}
                                            {!locked && isNewSub && (
                                              <TableRow>
                                                <TableCell
                                                  colSpan={4}
                                                  sx={{ pt: 1, pb: 1 }}
                                                >
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={12} md={4.5}>
                                                      <Select
                                                        options={
                                                          formattedLabOptions
                                                        }
                                                        value={subRow.lab_id}
                                                        onChange={(
                                                          selected,
                                                        ) => {
                                                          const updated = [
                                                            ...rows,
                                                          ];
                                                          updated[
                                                            index
                                                          ].compositeRows[
                                                            subIndex
                                                          ].lab_id = selected;
                                                          setRows(updated);
                                                        }}
                                                        placeholder="Choose lab"
                                                        menuPortalTarget={
                                                          document.body
                                                        }
                                                        styles={selectStyles}
                                                      />
                                                    </Grid>

                                                    <Grid item xs={6} md={2}>
                                                      <TextField
                                                        fullWidth
                                                        placeholder="Min Range"
                                                        value={subRow.min || ""}
                                                        sx={textfieldText}
                                                        onChange={(e) => {
                                                          const updated = [
                                                            ...rows,
                                                          ];
                                                          updated[
                                                            index
                                                          ].compositeRows[
                                                            subIndex
                                                          ].min =
                                                            e.target.value;
                                                          setRows(updated);
                                                        }}
                                                      />
                                                    </Grid>

                                                    <Grid item xs={6} md={2}>
                                                      <TextField
                                                        fullWidth
                                                        placeholder="Max Range"
                                                        value={subRow.max || ""}
                                                        sx={textfieldText}
                                                        onChange={(e) => {
                                                          const updated = [
                                                            ...rows,
                                                          ];
                                                          updated[
                                                            index
                                                          ].compositeRows[
                                                            subIndex
                                                          ].max =
                                                            e.target.value;
                                                          setRows(updated);
                                                        }}
                                                      />
                                                    </Grid>

                                                    <Grid item xs={12} md={3}>
                                                      <TextField
                                                        fullWidth
                                                        placeholder="Critical Range"
                                                        value={
                                                          subRow.critical || ""
                                                        }
                                                        sx={textfieldText}
                                                        onChange={(e) => {
                                                          const updated = [
                                                            ...rows,
                                                          ];
                                                          updated[
                                                            index
                                                          ].compositeRows[
                                                            subIndex
                                                          ].critical =
                                                            e.target.value;
                                                          setRows(updated);
                                                        }}
                                                      />
                                                    </Grid>
                                                  </Grid>
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </React.Fragment>
                                        );
                                      },
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            {rows.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  sx={{
                    ...primaryBtnTeal,
                    height: "45px",
                    minWidth: "180px",
                  }}
                  disabled={loading}
                  onClick={handleSubmitAll}
                >
                  {loading ? "Submitting..." : "Submit Report"}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      <Dialog
        open={createModal.open}
        onClose={closeCreateTypeModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Lato", fontWeight: 700 }}>
          Create Test
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Lato", mb: 1, color: "#667085" }}>
            Create <b>{createModal.label}</b> as:
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createChoice === "normal"}
                  onChange={() => setCreateChoice("normal")}
                  sx={{ "&.Mui-checked": { color: "neutral.teal" } }}
                />
              }
              label={<Typography sx={{ fontFamily: "Lato" }}>Test</Typography>}
            />
            {!createModal.isSub && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createChoice === "composite"}
                    onChange={() => setCreateChoice("composite")}
                    sx={{ "&.Mui-checked": { color: "neutral.teal" } }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: "Lato" }}>
                    Composite Test
                  </Typography>
                }
              />
            )}
          </Box>

          {!createModal.isSub && (
            <Typography
              sx={{ fontFamily: "Lato", mt: 1, fontSize: 12, color: "#667085" }}
            >
              * Composite test will show a sub-table for adding sub-tests.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeCreateTypeModal}
            sx={{ borderRadius: "10px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmCreateType}
            sx={{
              ...primaryBtnTeal,
              height: "36px",
              borderRadius: "10px",
              px: 2,
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiImageScanReport;
